import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@material-ui/core';
import { push } from 'connected-react-router';
import { ListParams } from '../../../types/main';
import { LoadingFrame, PaginationBar } from '../../../components';
import {
  selectHaveUploadMedia,
  selectArticles,
  selectArticlesFormDirty,
} from '../../../store/modules/articles/selectors';
import { getArticleListRequest, setArticleListDirty } from '../../../store/modules/articles/actions';
import { selectProject } from '../../../store/modules/projects/selectors';
import { getAllQueryData, stringifyQuery, getQueryFilter } from '../../../utility/helpers';
import { saveCommonProjectSetting, getCommonProjectSettings } from '../../../services/local-storage/projectStorage';
import { useActionDispatcher } from '../../../hooks';
import FilterBarMedia from './FilterBarMedia';
import { MediaListStyles } from './styles';
import FormAction from './actions/FormAction';
import MediaItem from './media/MediaItem';
import UploadMedia from './media/UploadMedia';
import MediaPreviewModal from './media/media-preview/MediaPreviewModal';
import MediaExportModal from './media/media-export/MediaExportModal';
import { selectCurrentUser } from '../../../store/modules/users/selectors';
import { setAllowChangeProjectStatus } from '../../../store/modules/projects/actions';
import MediaSortSelect from './MediaSortSelect';
import { Project } from '../../../types/items';
import { Option } from '../../../components/form-simple-autocomplete/FormSimpleAutoComplete';
import SearchBar from '../../../components/list-table/SearchBar';
import { getIncompleteArticles } from '../utils';
import { mediaStorage } from '../../../services/local-storage/mediaStorage';

type SortOption = Option & {
  sort_key: string;
  sort_dir: 'ASC' | 'DESC';
};

/*
  page 0
  page_size 5
  sort_key title
  sort_dir DESC
  filter[search]
  filter[project_id] 1
  filter[scene_id]
  filter[is_media] 1
*/

/* eslint-disable react/no-array-index-key */

const pageItems = [5, 11, 17, 23, 29];
const defaultPageSize = (window.screen.width >= 768) ? 29 : 5;

const saveToStoragePageSize = (pageSize: number) => {
  saveCommonProjectSetting('mediaPageSize', pageSize);
};

const MediaList = () => {
  const classes = MediaListStyles();
  const dispatch = useDispatch();
  const currentProject = useSelector(selectProject);
  const { data: currentUser } = useSelector(selectCurrentUser);
  const { data, loading, dirty } = useSelector(selectArticles);
  const fetch = useActionDispatcher(getArticleListRequest);
  const formDirty = useSelector(selectArticlesFormDirty);
  const haveUploadFiles = useSelector(selectHaveUploadMedia);
  const [sortingValue, setSortingValue] = useState(mediaStorage.getValues().sortOptionId);
  const [search, setSearch] = useState<string>(mediaStorage.getValues().search);
  const [incompleteOnly, setIncomplete] = useState<boolean>(false);
  const sortOptions: SortOption[] = [
    {
      id: 1,
      label: 'Upload order (newest first)',
      sort_key: 'ref.created_age',
      sort_dir: 'DESC',
    },
    {
      id: 2,
      label: 'Upload Order (oldest first)',
      sort_key: 'ref.created_age',
      sort_dir: 'ASC',
    },
    {
      id: 3,
      label: 'Title A-Z',
      sort_key: 'title',
      sort_dir: 'ASC',
    },
    {
      id: 4,
      label: 'Title Z-A',
      sort_key: 'title',
      sort_dir: 'DESC',
    },
  ];

  const mediaList = useMemo(() => {
    if (data?.items.length) {
      if (incompleteOnly) {
        return getIncompleteArticles(data.items);
      }
      return data.items;
    }
    return [];
  }, [data, incompleteOnly]);

  const getParams = useCallback((project: Project): ListParams => {
    const queryData = getAllQueryData(); // remove
    const mediaType = getQueryFilter('media_type_id', data?.filters?.active || [])?.value
      || queryData?.filter?.media_type_id;
    const commonProjectSettings = getCommonProjectSettings();
    const params: ListParams & {
      preview?: string;
      documentPageId?: string;
      is_general_comment?: boolean;
    } = {
      page: Number(queryData.page) || 0,
      sort_dir: queryData.sort_dir || sortOptions[sortingValue - 1].sort_dir,
      sort_key: queryData.sort_key || sortOptions[sortingValue - 1].sort_key,
      page_size: Number(queryData.page_size) || commonProjectSettings?.mediaPageSize || defaultPageSize,
      filter: {
        project_id: project.id,
        is_media: 1,
        media_type_id: mediaType,
        is_general_comment: false,
        search,
      },
      preview: queryData.preview,
      documentPageId: queryData.documentPageId
    };

    if (params!.filter!.media_type_id === undefined) {
      delete params!.filter!.media_type_id;
    }
    if (params.preview === undefined) {
      delete params.preview;
    }

    return params;
    // eslint-disable-next-line
  }, [sortOptions, data, search]);

  const handleChangeSort = useCallback((e) => {
    setSortingValue(e.target.value);

    if (currentProject.data) {
      const params = getParams(currentProject.data);
      const sortOption = sortOptions.find((opt) => opt.id === e.target.value) || sortOptions[0];
      mediaStorage.setValue('sortOptionId', e.target.value);

      params.sort_key = sortOption.sort_key;
      params.sort_dir = sortOption.sort_dir;

      dispatch(push({ search: stringifyQuery(params) }));
      dispatch(setArticleListDirty(true));
    }
  }, [setSortingValue, currentProject, dispatch, getParams, sortOptions]);

  const changeSearchHandler = useCallback((searchValue) => {
    if (currentProject.data) {
      setSearch(searchValue);
    }
  }, [currentProject]);

  // eslint-disable-next-line
  const incompleteChangeHandler = useCallback((val: boolean) => {
    setIncomplete(val);
  }, []);

  useEffect(() => {
    if (currentProject.data) {
      const params = getParams(currentProject.data);
      params.filter!.search = search;
      mediaStorage.setValue('search', search);
      dispatch(push({ search: stringifyQuery(params) }));
      dispatch(setArticleListDirty(true));
    }
    // eslint-disable-next-line
  }, [search, currentProject]);

  useEffect(() => {
    if (formDirty || haveUploadFiles) {
      dispatch(setAllowChangeProjectStatus(false));
    } else {
      dispatch(setAllowChangeProjectStatus(true));
    }
    return () => {
      dispatch(setAllowChangeProjectStatus(true));
    };
  }, [dispatch, formDirty, haveUploadFiles]);

  useEffect(() => {
    if (currentProject.data) {
      if (!loading) {
        const queryData = getAllQueryData();
        const params = getParams(currentProject.data);

        if (dirty || data === null) {
          if (currentUser) {
            dispatch(getArticleListRequest(params));
            dispatch(push({ search: stringifyQuery(params) }));
          }
        } else {
          const filter = getQueryFilter('project_id', data.filters.active);

          if (filter !== undefined && Number(filter.value) !== currentProject.data.id) {
            dispatch(getArticleListRequest(params));
          }
          if (queryData.filter === undefined) {
            dispatch(push({ search: stringifyQuery(params) }));
          }
        }
      }
    }
  }, [dispatch, data, loading, dirty, currentProject, currentUser, getParams]);

  if (data === null) {
    return <LoadingFrame />;
  }

  return (
    <Grid container spacing={4} className={classes.root}>
      <FilterBarMedia data={data} />
      {(loading || currentProject.loading) && <LoadingFrame />}
      <MediaExportModal />
      <Grid item xs={12} container justify="space-between" alignItems="center" className={classes.mediaBarContainer}>
        <Grid item sm={5} className={classes.mediaSearchContainer}>
          <SearchBar onChange={changeSearchHandler} value={search} />
        </Grid>
        <Grid item>
          <MediaSortSelect sortingValue={sortingValue} sortOptions={sortOptions} onSort={handleChangeSort} />
        </Grid>
        {/* 2020.11.18,bn: This feature will have the meaning of media items that are present in a script but
                           have not been uploaded.  So this code is disabled for now
        <Grid item>
          <Formik initialValues={{ only_incomplete: false }} onSubmit={() => {}}>
            <FormSwitch
              label="Show only incomplete"
              name="only_incomplete"
              checked={incompleteOnly}
              onChangeCallback={incompleteChangeHandler}
            />
          </Formik>
        </Grid>
        */}
      </Grid>
      <MediaPreviewModal mediaList={data.items} />
      {
        mediaList.map((item) => (
          <Grid item xs={6} sm={4} key={item.id} className={classes.mediaContainer}>
            <MediaItem media={item} />
          </Grid>
        ))
      }
      <Grid item xs={6} sm={4} key={data.items.length} className={classes.mediaContainer}>
        {currentProject?.data?.permissions.can_edit && <UploadMedia />}
      </Grid>
      <PaginationBar
        fetch={fetch}
        pagination={data.pagination}
        pageItems={pageItems}
        countItems={data.items.length}
        loading={(loading || currentProject.loading)}
        onChangePageSize={saveToStoragePageSize}
      />
      <FormAction />
    </Grid>
  );
};

export default MediaList;
